import { createSlice } from "@reduxjs/toolkit";

import {
  addCommentTdsSignOffReportAction,
  approveTdsSignOffReportAction,
  getAllTanNumberListAction,
  getSignOffTdsReportDetailsAction,
  getSignOffTdsReportListAction,
  getTdsPayableListAction,
  postSaveTdsEntryAction,
  signOffTdsReportAction,
} from "@/redux/tdsTcsManagement/actions";
import { initialState } from "@/redux/tdsTcsManagement/initialState";
import { tdsTcsManagementReducer } from "@/redux/tdsTcsManagement/reducer";
import type { TdsTcsTypes } from "@/redux/tdsTcsManagement/tdsTcsManagementType";

export const tdsTcsManagementSlice = createSlice({
  name: "tdsTcsManagement",
  initialState,
  reducers: tdsTcsManagementReducer,
  extraReducers(builder) {
    builder
      .addCase(getTdsPayableListAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getTdsPayableListAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const {
            success,
            message,
            status_code,
            result,
            total_count,
            total_tds,
            financial_year,
            total_taxable_amount,
            error,
          } = payload;

          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success,
              error: error,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            tdsPayableList: result,
            tdsPayableTotalCount: total_count,
            tdsPayableTotalTds: total_tds,
            tdsPayableFinancialYear: financial_year,
            tdsPayableTotalTaxAmount: total_taxable_amount,
          };
        }
      )
      .addCase(
        getTdsPayableListAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(postSaveTdsEntryAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        savingEntry: true,
      }))
      .addCase(
        postSaveTdsEntryAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code } = payload;

          if (success === 0) {
            return {
              ...state,
              savingEntry: false,
              success,
              error: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            savingEntry: false,
            success,
            message,
            statusCode: status_code,
          };
        }
      )
      .addCase(
        postSaveTdsEntryAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          savingEntry: false,
          error: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(getSignOffTdsReportListAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        tdsReportFetching: true,
      }))
      .addCase(
        getSignOffTdsReportListAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code, result } = payload;

          if (success === 0) {
            return {
              ...state,
              tdsReportFetching: false,
              success,
              tdsReportError: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            tdsReportFetching: false,
            success,
            statusCode: status_code,
            tdsReportMessage: message,
            tdsReportList: result,
          };
        }
      )
      .addCase(
        getSignOffTdsReportListAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          tdsReportFetching: false,
          tdsReportError: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(
        getSignOffTdsReportDetailsAction.pending,
        (state: TdsTcsTypes) => ({
          ...state,
          tdsReportFetching: true,
        })
      )
      .addCase(
        getSignOffTdsReportDetailsAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code, result } = payload;

          if (success === 0) {
            return {
              ...state,
              tdsReportFetching: false,
              success,
              tdsReportError: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            tdsReportFetching: false,
            success,
            statusCode: status_code,
            tdsReportMessage: message,
            tdsReportDetails: result,
          };
        }
      )
      .addCase(
        getSignOffTdsReportDetailsAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          tdsReportFetching: false,
          tdsReportError: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(approveTdsSignOffReportAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        approvingTdsReport: true,
      }))
      .addCase(
        approveTdsSignOffReportAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code } = payload;

          if (success === 0) {
            return {
              ...state,
              approvingTdsReport: false,
              success,
              tdsReportError: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            approvingTdsReport: false,
            success,
            statusCode: status_code,
            tdsReportMessage: message,
          };
        }
      )
      .addCase(
        approveTdsSignOffReportAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          approvingTdsReport: false,
          tdsReportError: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(
        addCommentTdsSignOffReportAction.pending,
        (state: TdsTcsTypes) => ({
          ...state,
          addingTdsReportComment: true,
        })
      )
      .addCase(
        addCommentTdsSignOffReportAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code } = payload;

          if (success === 0) {
            return {
              ...state,
              addingTdsReportComment: false,
              success,
              tdsReportError: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            addingTdsReportComment: false,
            success,
            statusCode: status_code,
            addCommentTdsReportMessage: message,
          };
        }
      )
      .addCase(
        addCommentTdsSignOffReportAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          addingTdsReportComment: false,
          tdsReportError: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(signOffTdsReportAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        signingOffTdsReport: true,
      }))
      .addCase(
        signOffTdsReportAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code } = payload;

          if (success === 0) {
            return {
              ...state,
              signingOffTdsReport: false,
              success,
              tdsReportError: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            signingOffTdsReport: false,
            success,
            statusCode: status_code,
            tdsReportMessage: message,
          };
        }
      )
      .addCase(
        signOffTdsReportAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          signingOffTdsReport: false,
          tdsReportError: error?.message ?? null,
          success: 0,
        })
      )
      .addCase(getAllTanNumberListAction.pending, (state: TdsTcsTypes) => ({
        ...state,
        fetching: true,
      }))
      .addCase(
        getAllTanNumberListAction.fulfilled,
        (state: TdsTcsTypes, { payload }) => {
          const { success, message, status_code, result } = payload;

          if (success === 0) {
            return {
              ...state,
              fetching: false,
              success,
              error: message,
              statusCode: status_code,
            };
          }

          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            message,
            tanNumberList: result,
          };
        }
      )
      .addCase(
        getAllTanNumberListAction.rejected,
        (state: TdsTcsTypes, { error }) => ({
          ...state,
          fetching: false,
          error: error?.message ?? null,
          success: 0,
        })
      );
  },
});

export const {
  resetTdsTcsManagementError,
  resetTdsTcsManagementSuccess,
  resetTdsTcsManagementMessage,
  resetTdsTcsManagementStatusCode,
  resetTdsTcsManagementFetching,
  resetTdsPayableList,
  resetSavingEntryStatus,
  resetTdsReportError,
  resetTdsReportMessage,
  resetTdsReportFetching,
  resetTdsReportList,
  resetTdsReportDetails,
  resetApprovingTdsReportStatus,
  resetAddingTdsReportCommentStatus,
  resetAddCommentTdsReportMessage,
  resetSigningOffTdsReportStatus,
  resetTanNumberList,
} = tdsTcsManagementSlice.actions;
